import {
  IrisBankAccount,
  IrisBalance,
  IrisConsent,
  IrisTransaction,
  IrisConsentStatus,
  IrisBalanceType,
  IrisCreditDebitIndicator,
} from '@app/core/types/iris-report';
import { Decimal } from '@app/core/utils/decimal';

export class BankAccountView {
  account: IrisBankAccount;
  brand: string;
  balance: number | null;
  balanceCurrency: string;
  balances: IrisBalance[];
  consent: IrisConsent | null;
  consentStatusColor!: string;
  consentStatusLabel!: string;
  transactions: IrisTransaction[];
  totalIncoming31Days: number;
  totalOutgoing31Days: number;
  expanded: boolean;
  isDifferentBrand: boolean;

  constructor(
    account: IrisBankAccount,
    brand: string,
    clientBrandCode: string
  ) {
    this.account = account;
    this.brand = brand;
    this.isDifferentBrand = brand !== clientBrandCode;

    this.consent =
      account.consents?.find(
        (c) =>
          c.status === IrisConsentStatus.VALID ||
          c.status === IrisConsentStatus.AUTHORISED
      ) ||
      account.consents?.[0] ||
      null;

    this.resolveConsentDisplay();

    const availableBalance =
      account.balances?.find(
        (b) => b.balanceType === IrisBalanceType.INTERIM_AVAILABLE
      ) || account.balances?.[0];

    this.balance = availableBalance?.amount ?? null;
    this.balanceCurrency = availableBalance?.currency ?? account.currency;
    this.balances = account.balances || [];
    this.transactions = account.transactions || [];
    this.expanded = false;

    const { totalIncoming, totalOutgoing } = this.calculateTurnover();
    this.totalIncoming31Days = totalIncoming;
    this.totalOutgoing31Days = totalOutgoing;
  }

  private resolveConsentDisplay(): void {
    if (!this.consent) {
      this.consentStatusColor = 'warn';
      this.consentStatusLabel = 'bankInfo.consent.noConsent';
      return;
    }

    const statusMap: { [key: string]: { color: string; label: string } } = {
      [IrisConsentStatus.VALID]: {
        color: 'accent',
        label: 'bankInfo.consent.statusValid',
      },
      [IrisConsentStatus.AUTHORISED]: {
        color: 'accent',
        label: 'bankInfo.consent.statusAuthorised',
      },
      [IrisConsentStatus.RECEIVED]: {
        color: 'primary',
        label: 'bankInfo.consent.statusReceived',
      },
      [IrisConsentStatus.REJECTED]: {
        color: 'warn',
        label: 'bankInfo.consent.statusRejected',
      },
      [IrisConsentStatus.EXPIRED]: {
        color: 'warn',
        label: 'bankInfo.consent.statusExpired',
      },
      [IrisConsentStatus.REVOKED_BY_PSU]: {
        color: 'warn',
        label: 'bankInfo.consent.statusRevoked',
      },
      [IrisConsentStatus.TERMINATED_BY_TPP]: {
        color: 'warn',
        label: 'bankInfo.consent.statusTerminated',
      },
      [IrisConsentStatus.PARTIALLY_AUTHORISED]: {
        color: 'primary',
        label: 'bankInfo.consent.statusPartial',
      },
    };

    const config = statusMap[this.consent.status] || {
      color: 'warn',
      label: 'bankInfo.consent.statusUnknown',
    };
    this.consentStatusColor = config.color;
    this.consentStatusLabel = config.label;
  }

  private calculateTurnover(): {
    totalIncoming: number;
    totalOutgoing: number;
  } {
    const now = new Date();
    const thirtyOneDaysAgo = new Date(now.getTime() - 31 * 24 * 60 * 60 * 1000);

    const incomingAmounts: number[] = [];
    const outgoingAmounts: number[] = [];

    for (const txn of this.transactions) {
      const txnDate = new Date(txn.bookingDate);
      if (txnDate >= thirtyOneDaysAgo) {
        const amount = Math.abs(txn.transactionAmount.amount);
        if (txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT) {
          incomingAmounts.push(amount);
        } else {
          outgoingAmounts.push(amount);
        }
      }
    }

    return {
      totalIncoming: Decimal.sumNumbers(...incomingAmounts),
      totalOutgoing: Decimal.sumNumbers(...outgoingAmounts),
    };
  }

  get turnover(): number {
    return Decimal.sumNumbers(
      this.totalIncoming31Days,
      this.totalOutgoing31Days
    );
  }
}
